import { Store, UpdateTimeOffParams, makeZonedDateTime } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { EditTimeOffRequestProps } from 'home/stack/types'
import moment from 'moment'
import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { useAddTimeOffRequest } from 'request_time_off/queries/add'
import { useTimeOffRequest } from 'request_time_off/queries/detail'
import { useUpdateTimeOffRequest } from 'request_time_off/queries/update'
import { errorAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { showHeaderTitle } from 'shared/header/header_title'
import { useRecentStoresContext } from 'stores/context'
import { useHelperStores } from 'stores/queries/list'
import { useUser } from 'users/queries/detail'
import { EditRequestTimeOffView } from './views/edit_time_off_request'

export function EditRequestTimeOffScreen({
  navigation,
  route,
}: EditTimeOffRequestProps) {
  const timeOffRequestQuery = useTimeOffRequest(
    {
      id: route.params?.timeOffEventId,
    },
    !!route.params?.timeOffEventId
  )

  const { handleNavigation } = useRoutingContext()
  const { theme } = useTheme()
  const { data: user } = useUser()
  const { homeStoreId } = useRecentStoresContext()
  const storeQuery = useHelperStores()
  const store = storeQuery.data?.stores.find((s: Store) => s.id === homeStoreId)
  const editTimeOffRequestMutation = useUpdateTimeOffRequest()
  const addTimeOffRequestMutation = useAddTimeOffRequest()

  const initialTimeOffParams: UpdateTimeOffParams = useMemo(
    () =>
      timeOffRequestQuery.data
        ? {
            id: Number(timeOffRequestQuery.data.id),
            store_id: Number(homeStoreId),
            user_id: Number(user?.id),
            start_time: makeZonedDateTime(
              timeOffRequestQuery.data.startDate,
              timeOffRequestQuery.data.startTime,
              timeOffRequestQuery.data.tz
            ),
            end_time: makeZonedDateTime(
              timeOffRequestQuery.data.endDate,
              timeOffRequestQuery.data.endTime,
              timeOffRequestQuery.data.tz
            ),
            reason: timeOffRequestQuery.data.reason,
          }
        : {
            id: undefined,
            store_id: Number(homeStoreId),
            user_id: Number(user?.id),
            start_time: '',
            end_time: '',
            reason: '',
          },
    [homeStoreId, timeOffRequestQuery, user]
  )

  const [timeOffParams, setTimeOffParams] =
    useState<UpdateTimeOffParams>(initialTimeOffParams)
  const [attestationCheck, setAttestationCheck] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)

  useLayoutEffect(() => {
    showHeaderTitle(
      navigation,
      route.params?.timeOffEventId
        ? t('editTimeOffRequest')
        : t('requestTimeOff')
    )
  }, [navigation, route.params?.timeOffEventId, theme])

  const canSave = useMemo(() => {
    if (
      moment(timeOffParams.start_time).isSame(moment(timeOffParams.end_time)) ||
      !timeOffParams.reason?.length ||
      !attestationCheck ||
      moment(timeOffParams.start_time).isBefore(
        moment(user?.lastScheduledDate).add(1, 'day')
      )
    ) {
      return false
    }
    return true
  }, [
    attestationCheck,
    timeOffParams.end_time,
    timeOffParams.reason?.length,
    timeOffParams.start_time,
    user?.lastScheduledDate,
  ])

  const onEdit = useCallback(
    async (params?: UpdateTimeOffParams) => {
      setLoading(true)
      try {
        await editTimeOffRequestMutation.mutateAsync(
          params
            ? params
            : {
                ...initialTimeOffParams,
                ...timeOffParams,
              }
        )
        setLoading(false)
        handleNavigation({
          routeName: 'schedule',
          routeParams: {
            date: timeOffRequestQuery.data?.startDate,
          },
        })
      } catch (err: any) {
        setLoading(false)
        errorAlert(err, t('editShiftErrorTitle'))
      }
    },
    [
      editTimeOffRequestMutation,
      initialTimeOffParams,
      timeOffParams,
      handleNavigation,
      timeOffRequestQuery.data?.startDate,
    ]
  )

  const onAdd = useCallback(async () => {
    setLoading(true)
    try {
      await addTimeOffRequestMutation.mutateAsync({
        ...timeOffParams,
        user_id: Number(user?.id),
        approver_user_id: undefined,
        status: 'pending',
        id: undefined,
      })
      setLoading(false)
      handleNavigation({
        routeName: 'schedule',
        routeParams: {
          date: timeOffRequestQuery.data?.startDate,
        },
      })
    } catch (err: any) {
      setLoading(false)
      console.log(err)
      errorAlert(err, t('editTimeOffErrorTitle'))
    }
  }, [
    addTimeOffRequestMutation,
    timeOffParams,
    user?.id,
    handleNavigation,
    timeOffRequestQuery.data?.startDate,
  ])

  const onSubmit = timeOffRequestQuery.data ? onEdit : onAdd

  const onDelete = () => {
    return onEdit({ ...initialTimeOffParams, deleted: true })
  }

  useLayoutEffect(() => {
    showHeaderButton({
      navigation,
      disabled: !canSave,
      onPress: () => onSubmit(),
      theme,
      title: route.params?.timeOffEventId ? t('save') : t('create'),
    })
  }, [
    canSave,
    navigation,
    onAdd,
    onSubmit,
    route.params?.timeOffEventId,
    theme,
  ])

  if (!user) {
    return null
  }

  return (
    <StatusBoundary
      isLoading={timeOffRequestQuery.isLoading}
      error={timeOffRequestQuery.error}
    >
      <EditRequestTimeOffView
        store={store}
        timeOffEvent={timeOffRequestQuery.data}
        attestationCheck={attestationCheck}
        setAttestationCheck={setAttestationCheck}
        loading={loading}
        onDelete={onDelete}
        onTimeOffEventParamsChanged={setTimeOffParams}
        lastScheduledDate={user.lastScheduledDate}
      />
    </StatusBoundary>
  )
}
